import React from 'react';
import moment, { Moment } from 'moment';
import { Form, Spin, DatePicker, Select, Input, Button } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import {
  AbsencePeriod,
  ReplacementType,
  Replacer
} from '../../../../core-module/services/absence/types';
import { ReplacementFormValues } from './types';
import { isBeforeAllowedAbsenceDate } from './helpers';

const PHONE_REGEX = /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)/;

interface Props {
  readonly form: WrappedFormUtils<ReplacementFormValues>;
  readonly absencePeriod: AbsencePeriod;
  readonly soonestAllowedAbsenceDate: Moment;
  readonly replacers: Replacer[];
  readonly saving: boolean;
  readonly onSave: () => void;
}

const ReplacementFormComponent: React.FC<Props> = ({
  form,
  absencePeriod,
  soonestAllowedAbsenceDate,
  replacers,
  saving,
  onSave
}) => {
  const validatePhoneNumber = (rule: any, value: string, callback: (message?: string) => void) => {
    if (value && !PHONE_REGEX.test(value)) {
      callback('Telefoonnummer is ongeldig');
    } else {
      callback();
    }
  };

  const disabledDate = (currentDate: Moment | null) => {
    if (currentDate === null) {
      return true;
    }

    return (
      currentDate.isBefore(absencePeriod.from, 'day') ||
      currentDate.isAfter(absencePeriod.to, 'day') ||
      isBeforeAllowedAbsenceDate(currentDate, soonestAllowedAbsenceDate)
    );
  };

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave();
  };

  return (
    <Spin spinning={saving}>
      <Form layout='inline' onSubmit={handleSave}>
        <Form.Item style={{ marginBottom: 0, marginRight: '5px' }}>
          {form.getFieldDecorator('date', {
            rules: [{ required: true, message: 'Voer een dag in' }],
            initialValue: undefined
          })(
            <DatePicker
              format='dddd D MMMM YYYY'
              showToday={false}
              defaultPickerValue={moment(absencePeriod.from)}
              disabledDate={disabledDate}
            />
          )}
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginRight: '5px' }}>
          {form.getFieldDecorator('type', {
            validateTrigger: ['onBlur', 'onChange'],
            rules: [{ required: true, message: 'Type is verplicht' }],
            initialValue: 'Driver'
          })(
            <Select<ReplacementType> style={{ width: '100px' }} placeholder='Type'>
              <Select.Option value='Driver'>Bezorger</Select.Option>
              <Select.Option value='Acquaintance'>Kennis</Select.Option>
            </Select>
          )}
        </Form.Item>

        {form.getFieldValue('type') === 'Driver' && (
          <Form.Item style={{ marginBottom: 0, marginRight: '5px' }}>
            {form.getFieldDecorator('replacementDriverId', {
              validateTrigger: ['onBlur', 'onChange'],
              rules: [{ required: true, message: 'Vervanger is verplicht' }],
              initialValue: undefined
            })(
              <Select<ReplacementType>
                style={{ width: '150px' }}
                placeholder='Vervanger'
                showSearch
                optionFilterProp='children'
              >
                {replacers.map((replacer, index) => (
                  <Select.Option key={index} value={replacer.driverId}>
                    {replacer.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        )}

        {form.getFieldValue('type') === 'Acquaintance' && (
          <>
            <Form.Item style={{ marginBottom: 0, marginRight: '5px' }}>
              {form.getFieldDecorator('acquaintanceName', {
                validateTrigger: 'onBlur',
                rules: [
                  { required: true, message: 'Naam is verplicht' },
                  { max: 255, message: 'Naam mag 255 karakters bevatten' }
                ],
                initialValue: undefined
              })(<Input placeholder='Naam' />)}
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginRight: '5px' }}>
              {form.getFieldDecorator('acquaintancePhoneNumber', {
                validateTrigger: 'onBlur',
                rules: [
                  { required: true, message: 'Telefoonnummer is verplicht' },
                  { validator: validatePhoneNumber }
                ],
                initialValue: undefined
              })(<Input placeholder='Telefoonnummer' />)}
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button type='primary' htmlType='submit' loading={saving}>
            Toevoegen
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export { ReplacementFormComponent };
